import { useEffect, useMemo, useState } from "react";
import { collection, collectionGroup, getDocs, query } from "firebase/firestore";
import { db } from "./firebase";
import { calculateRecordFee, canonicalTeamName } from "./utils/feeRules";
import useTeamDirectory, { getTeamRealName, formatTeamWithRealName } from "./hooks/useTeamDirectory";
import "./TopEarners.css";

const fmt = (n) => Number(n || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
const money = (n) => `฿${fmt(n)}`;

const monthNames = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const monthShort = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const RANK_METRICS = {
  totalReceived: { label: "ทีมได้รับทั้งหมด", short: "รับทั้งหมด", suffix: "บาท" },
  pressNet: { label: "ค่ากดสุทธิ", short: "ค่ากดสุทธิ", suffix: "บาท" },
  extraNet: { label: "ค่าคอม / รายได้พิเศษ", short: "ค่าคอม+พิเศษ", suffix: "บาท" },
  shopCut: { label: "สร้างรายได้ให้ร้าน", short: "ร้านได้", suffix: "บาท" },
  qty: { label: "จำนวนบัตร", short: "จำนวนบัตร", suffix: "ใบ" },
};

const toDateSafe = (record) => {
  if (typeof record?.createdAt?.toDate === "function") return record.createdAt.toDate();
  for (const value of [record?.createdAt, record?.date, record?.pressDate]) {
    if (!value) continue;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const feeParts = (record) => {
  const fee = calculateRecordFee(record);
  return {
    gross: fee.baseFee,
    shopCut: fee.deducted,
    beforeWht: fee.teamBeforeTax,
    wht: fee.tax,
    net: fee.net,
  };
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const extraAmounts = (item = {}) => {
  const whtRate = Number.isFinite(Number(item.whtRate)) ? Number(item.whtRate) : 0.03;

  if (item.taxBorneByCompany) {
    const net = roundMoney(item.teamReceivesAmount ?? item.netAmount ?? item.amount ?? 0);
    const whtBase = roundMoney(item.whtBaseAmount ?? net);
    const wht = roundMoney(item.withholdingAmount ?? item.whtAmount ?? whtBase * whtRate);
    const gross = roundMoney(item.companyCostAmount ?? net + wht);
    return { gross, wht, net };
  }

  const gross = roundMoney(item.grossAmount ?? item.amount ?? 0);
  const wht = roundMoney(item.withholdingAmount ?? item.whtAmount ?? gross * whtRate);
  const net = roundMoney(item.netAmount ?? gross - wht);
  return { gross, wht, net };
};

const extraDateSafe = (item) => {
  for (const value of [item?.createdAt, item?.date]) {
    if (!value) continue;
    if (typeof value?.toDate === "function") return value.toDate();
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const sumRecords = (records = []) => records.reduce(
  (acc, record) => {
    const p = feeParts(record);
    acc.qty += Number(record.quantity || 0);
    acc.gross += p.gross;
    acc.shopCut += p.shopCut;
    acc.beforeWht += p.beforeWht;
    acc.wht += p.wht;
    acc.net += p.net;
    acc.count += 1;
    return acc;
  },
  { qty: 0, gross: 0, shopCut: 0, beforeWht: 0, wht: 0, net: 0, count: 0 }
);

const groupByEvent = (records = []) => {
  const map = new Map();
  records.forEach((record) => {
    const name = record.eventName || "ไม่ระบุชื่องาน";
    const current = map.get(name) || {
      name,
      records: [],
      qty: 0,
      gross: 0,
      net: 0,
      wht: 0,
      shopCut: 0,
    };
    const p = feeParts(record);
    current.records.push(record);
    current.qty += Number(record.quantity || 0);
    current.gross += p.gross;
    current.net += p.net;
    current.wht += p.wht;
    current.shopCut += p.shopCut;
    map.set(name, current);
  });
  return [...map.values()].sort((a, b) => b.net - a.net);
};

const rankIcon = (rank) => ({ 1: "🥇", 2: "🥈", 3: "🥉" }[rank] || `#${rank}`);
const metricValue = (team, metric) => Number(team?.[metric] || 0);
const metricText = (team, metric) => metric === "qty" ? `${fmt(team.qty)} ใบ` : money(team[metric]);

export default function TopEarners() {
  const now = new Date();
  const currentYear = String(now.getFullYear());
  const currentMonth = now.getMonth() + 1;

  const [records, setRecords] = useState([]);
  const [extraIncomes, setExtraIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [monthFilter, setMonthFilter] = useState(String(currentMonth));
  const [selectedTeam, setSelectedTeam] = useState("");
  const [rankMetric, setRankMetric] = useState("totalReceived");
  const [detailMonth, setDetailMonth] = useState(null);
  const { realNameByTeam } = useTeamDirectory();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [recordSnap, extraSnap] = await Promise.all([
          getDocs(query(collectionGroup(db, "records"))),
          getDocs(collection(db, "team-extra-income")),
        ]);
        if (!mounted) return;
        setRecords(
          recordSnap.docs
            .map((doc) => {
              const data = doc.data();
              return { ...data, __date: toDateSafe(data), __id: doc.id };
            })
            .filter((record) => record.__date)
        );
        setExtraIncomes(
          extraSnap.docs
            .map((doc) => {
              const data = doc.data();
              return { ...data, __date: extraDateSafe(data), __id: doc.id };
            })
            .filter((item) => item.__date)
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const yearOptions = useMemo(() => {
    const years = new Set([
      ...records.map((r) => String(r.__date.getFullYear())),
      ...extraIncomes.map((item) => String(item.__date.getFullYear())),
    ]);
    years.add("2026");
    years.add("2027");
    years.add(currentYear);
    return [...years].sort((a, b) => Number(b) - Number(a));
  }, [records, extraIncomes, currentYear]);

  useEffect(() => {
    if (!yearOptions.includes(selectedYear)) setSelectedYear(yearOptions[0] || currentYear);
  }, [yearOptions, selectedYear, currentYear]);

  const yearRecords = useMemo(
    () => records.filter((r) => String(r.__date.getFullYear()) === String(selectedYear)),
    [records, selectedYear]
  );

  const yearExtras = useMemo(
    () => extraIncomes.filter((item) => String(item.__date.getFullYear()) === String(selectedYear)),
    [extraIncomes, selectedYear]
  );

  const viewRecords = useMemo(() => {
    if (monthFilter === "ALL") return yearRecords;
    const month = Number(monthFilter);
    return yearRecords.filter((r) => r.__date.getMonth() + 1 === month);
  }, [yearRecords, monthFilter]);

  const viewExtras = useMemo(() => {
    if (monthFilter === "ALL") return yearExtras;
    const month = Number(monthFilter);
    return yearExtras.filter((item) => item.__date.getMonth() + 1 === month);
  }, [yearExtras, monthFilter]);

  const baseRanking = useMemo(() => {
    const grouped = new Map();
    const ensureRow = (teamName) => {
      const name = canonicalTeamName(teamName) || "ไม่ระบุ";
      if (name === "ยย") return null;
      if (!grouped.has(name)) {
        grouped.set(name, {
          name,
          qty: 0,
          count: 0,
          extraCount: 0,
          gross: 0,
          shopCut: 0,
          beforeWht: 0,
          wht: 0,
          pressNet: 0,
          extraGross: 0,
          extraWht: 0,
          extraNet: 0,
          totalReceived: 0,
        });
      }
      return grouped.get(name);
    };

    viewRecords.forEach((record) => {
      const row = ensureRow(record.teamName);
      if (!row) return;
      const p = feeParts(record);
      row.qty += Number(record.quantity || 0);
      row.count += 1;
      row.gross += p.gross;
      row.shopCut += p.shopCut;
      row.beforeWht += p.beforeWht;
      row.wht += p.wht;
      row.pressNet += p.net;
    });

    viewExtras.forEach((item) => {
      const row = ensureRow(item.teamName);
      if (!row) return;
      const p = extraAmounts(item);
      row.extraCount += 1;
      row.extraGross += p.gross;
      row.extraWht += p.wht;
      row.extraNet += p.net;
    });

    grouped.forEach((row) => {
      row.totalReceived = row.pressNet + row.extraNet;
    });

    return [...grouped.values()];
  }, [viewRecords, viewExtras]);

  const ranking = useMemo(
    () => [...baseRanking].sort((a, b) => metricValue(b, rankMetric) - metricValue(a, rankMetric)),
    [baseRanking, rankMetric]
  );

  const rankingTotal = useMemo(
    () => ranking.reduce((sum, row) => sum + metricValue(row, rankMetric), 0),
    [ranking, rankMetric]
  );
  const maxMetric = metricValue(ranking[0], rankMetric);

  const summary = useMemo(() => {
    return baseRanking.reduce(
      (acc, row) => ({
        teamCount: acc.teamCount + 1,
        totalQty: acc.totalQty + row.qty,
        totalGross: acc.totalGross + row.gross,
        totalWht: acc.totalWht + row.wht + row.extraWht,
        shopIncome: acc.shopIncome + row.shopCut,
        pressPayout: acc.pressPayout + row.pressNet,
        extraPayout: acc.extraPayout + row.extraNet,
        teamPayout: acc.teamPayout + row.totalReceived,
        extraCount: acc.extraCount + row.extraCount,
      }),
      { teamCount: 0, totalQty: 0, totalGross: 0, totalWht: 0, shopIncome: 0, pressPayout: 0, extraPayout: 0, teamPayout: 0, extraCount: 0 }
    );
  }, [baseRanking]);

  const selectedTeamRecords = useMemo(() => {
    if (!selectedTeam) return [];
    return yearRecords.filter((r) => canonicalTeamName(r.teamName) === selectedTeam);
  }, [selectedTeam, yearRecords]);

  const selectedTeamExtras = useMemo(() => {
    if (!selectedTeam) return [];
    return yearExtras.filter((item) => canonicalTeamName(item.teamName) === selectedTeam);
  }, [selectedTeam, yearExtras]);

  const selectedTeamYear = useMemo(() => {
    const press = sumRecords(selectedTeamRecords);
    const extra = selectedTeamExtras.reduce((acc, item) => {
      const p = extraAmounts(item);
      acc.gross += p.gross;
      acc.wht += p.wht;
      acc.net += p.net;
      acc.count += 1;
      return acc;
    }, { gross: 0, wht: 0, net: 0, count: 0 });
    return { ...press, extraGross: extra.gross, extraWht: extra.wht, extraNet: extra.net, extraCount: extra.count, totalReceived: press.net + extra.net, totalWht: press.wht + extra.wht };
  }, [selectedTeamRecords, selectedTeamExtras]);

  const selectedTeamMonths = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const monthRecords = selectedTeamRecords.filter((r) => r.__date.getMonth() + 1 === month);
      const monthExtras = selectedTeamExtras.filter((item) => item.__date.getMonth() + 1 === month);
      const press = sumRecords(monthRecords);
      const extra = monthExtras.reduce((acc, item) => {
        const p = extraAmounts(item);
        acc.gross += p.gross;
        acc.wht += p.wht;
        acc.net += p.net;
        return acc;
      }, { gross: 0, wht: 0, net: 0 });
      return {
        month,
        records: monthRecords,
        extras: monthExtras,
        ...press,
        extraGross: extra.gross,
        extraWht: extra.wht,
        extraNet: extra.net,
        extraCount: monthExtras.length,
        totalReceived: press.net + extra.net,
        totalWht: press.wht + extra.wht,
        totalCount: press.count + monthExtras.length,
      };
    });
  }, [selectedTeamRecords, selectedTeamExtras]);

  useEffect(() => {
    if (!selectedTeam) setDetailMonth(null);
    else if (monthFilter !== "ALL") setDetailMonth(Number(monthFilter));
    else setDetailMonth(null);
  }, [selectedTeam, monthFilter]);

  const detailMonthData = useMemo(
    () => selectedTeamMonths.find((m) => m.month === detailMonth) || null,
    [selectedTeamMonths, detailMonth]
  );

  const detailEvents = useMemo(
    () => detailMonthData ? groupByEvent(detailMonthData.records) : [],
    [detailMonthData]
  );

  const teamsForSelect = useMemo(
    () => [...baseRanking].sort((a, b) => a.name.localeCompare(b.name, "th")).map((r) => r.name),
    [baseRanking]
  );

  const periodLabel = monthFilter === "ALL"
    ? `ปี ${Number(selectedYear) + 543}`
    : `${monthNames[Number(monthFilter) - 1]} ${Number(selectedYear) + 543}`;

  return (
    <div className="te-page">
      <header className="te-hero">
        <div>
          <div className="te-eyebrow">TEAM PERFORMANCE</div>
          <h1>ผลงานทีม</h1>
          <p>รวมประวัติค่ากด ค่าคอม และรายได้พิเศษของทีมไว้ในที่เดียว พร้อมยอดที่ทีมได้รับจริง</p>
        </div>
        <div className="te-period-badge">{periodLabel}</div>
      </header>

      <section className="te-filter-card">
        <div className="te-filter-grid">
          <label>
            <span>ปี</span>
            <select value={selectedYear} onChange={(e) => { setSelectedYear(e.target.value); setSelectedTeam(""); }}>
              {yearOptions.map((year) => <option key={year} value={year}>{Number(year) + 543}</option>)}
            </select>
          </label>
          <label>
            <span>ช่วงเวลา</span>
            <select value={monthFilter} onChange={(e) => { setMonthFilter(e.target.value); setSelectedTeam(""); }}>
              <option value="ALL">ทั้งปี</option>
              {monthNames.map((name, index) => <option key={name} value={String(index + 1)}>{name}</option>)}
            </select>
          </label>
          <label>
            <span>เปิดรายละเอียดทีม</span>
            <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
              <option value="">เลือกทีม</option>
              {teamsForSelect.map((team) => <option key={team} value={team}>{formatTeamWithRealName(team, realNameByTeam)}</option>)}
            </select>
          </label>
          <button
            className="te-this-month-btn"
            onClick={() => { setSelectedYear(currentYear); setMonthFilter(String(currentMonth)); setSelectedTeam(""); }}
          >
            เดือนนี้
          </button>
        </div>
      </section>

      <section className="te-summary-grid">
        <div className="te-summary-card te-summary-card--primary">
          <span>ทีมได้รับทั้งหมด</span>
          <strong>{money(summary.teamPayout)}</strong>
          <small>ค่ากด {money(summary.pressPayout)} + ค่าคอม/พิเศษ {money(summary.extraPayout)}</small>
        </div>
        <div className="te-summary-card">
          <span>ค่าคอม / รายได้พิเศษทีม</span>
          <strong>{money(summary.extraPayout)}</strong>
          <small>{fmt(summary.extraCount)} รายการ · รวมตามประวัติที่แอดมินบันทึก</small>
        </div>
        <div className="te-summary-card">
          <span>ส่วนแบ่งที่ทีมสร้างให้ร้าน</span>
          <strong>{money(summary.shopIncome)}</strong>
          <small>ค่าคอมที่หักจากรายการค่ากด</small>
        </div>
        <div className="te-summary-card">
          <span>จำนวนบัตร</span>
          <strong>{fmt(summary.totalQty)}</strong>
          <small>{fmt(baseRanking.reduce((sum, row) => sum + row.count, 0))} รายการ</small>
        </div>
        <div className="te-summary-card">
          <span>หัก ณ ที่จ่าย</span>
          <strong>{money(summary.totalWht)}</strong>
          <small>ไม่ใช่รายได้ของร้าน</small>
        </div>
      </section>

      <section className="te-rank-toolbar">
        <div>
          <div className="te-section-kicker">LEADERBOARD</div>
          <h2>จัดอันดับตามอะไร</h2>
        </div>
        <div className="te-segmented" role="group" aria-label="เลือกเกณฑ์จัดอันดับ">
          {Object.entries(RANK_METRICS).map(([key, item]) => (
            <button
              key={key}
              type="button"
              className={rankMetric === key ? "is-active" : ""}
              onClick={() => setRankMetric(key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      {loading ? (
        <div className="te-state-card">กำลังโหลดข้อมูล…</div>
      ) : ranking.length === 0 ? (
        <div className="te-state-card">
          <strong>ยังไม่มีข้อมูลในช่วงนี้</strong>
          <span>ลองเปลี่ยนเดือนหรือปีด้านบน</span>
        </div>
      ) : (
        <>
          <section className="te-section-head te-section-head--compact">
            <div>
              <h2>Top 3 ของช่วงนี้</h2>
              <p>เรียงตาม {RANK_METRICS[rankMetric].label}</p>
            </div>
            <span>{ranking.length} ทีม</span>
          </section>

          <section className="te-podium-grid">
            {ranking.slice(0, 3).map((team, index) => {
              const current = metricValue(team, rankMetric);
              const share = rankingTotal > 0 ? (current / rankingTotal) * 100 : 0;
              return (
                <button key={team.name} className={`te-podium te-podium--${index + 1}`} onClick={() => setSelectedTeam(team.name)}>
                  <div className="te-podium-top">
                    <span className="te-rank-mark">{rankIcon(index + 1)}</span>
                    <span className="te-share-pill">{share.toFixed(1)}%</span>
                  </div>
                  <div className="te-podium-name">{team.name}</div>
                  {getTeamRealName(team.name, realNameByTeam) && <div className="te-podium-real-name">{getTeamRealName(team.name, realNameByTeam)}</div>}
                  <div className="te-podium-label">{RANK_METRICS[rankMetric].short}</div>
                  <div className="te-podium-net">{metricText(team, rankMetric)}</div>
                  <div className="te-podium-meta">
                    <span>ค่ากด {money(team.pressNet)}</span>
                    <span>ค่าคอม/พิเศษ {money(team.extraNet)}</span>
                    <span>ร้านได้ {money(team.shopCut)}</span>
                    <span>{fmt(team.qty)} ใบ</span>
                  </div>
                </button>
              );
            })}
          </section>

          <section className="te-section-head te-section-head--ranking">
            <div>
              <div className="te-section-kicker">ALL TEAMS</div>
              <h2>อันดับทั้งหมด</h2>
            </div>
            <p>หน้านี้เน้นผลงานคน — นับตามวันที่กรอกในเดือนที่เลือก รวมทั้งที่โอนแล้วและยังไม่โอน · ตัวเลขนี้ไม่ใช่ยอดรอบโอน (ดูยอดโอนที่ “โอนค่ากด”) · กำไรร้านรวมไปดูที่ “การเงิน & รายงาน”</p>
          </section>

          <section className="te-ranking-list">
            {ranking.map((team, index) => {
              const current = metricValue(team, rankMetric);
              const width = maxMetric > 0 ? Math.max(4, (current / maxMetric) * 100) : 0;
              const share = rankingTotal > 0 ? (current / rankingTotal) * 100 : 0;
              return (
                <button key={team.name} className="te-ranking-row" onClick={() => setSelectedTeam(team.name)}>
                  <div className="te-ranking-rank">{rankIcon(index + 1)}</div>
                  <div className="te-ranking-main">
                    <div className="te-ranking-title-row">
                      <div className="te-team-name-stack">
                        <strong>{team.name}</strong>
                        {getTeamRealName(team.name, realNameByTeam) && <small>{getTeamRealName(team.name, realNameByTeam)}</small>}
                      </div>
                      <span>{share.toFixed(1)}% ของเกณฑ์ที่เลือก</span>
                    </div>
                    <div className="te-progress"><i style={{ width: `${width}%` }} /></div>
                    <div className="te-ranking-meta">
                      <span>{fmt(team.qty)} ใบ</span>
                      <span>{team.count} ค่ากด</span>
                      <span>{team.extraCount} ค่าคอม/พิเศษ</span>
                      <span>ค่ากดรวม {money(team.gross)}</span>
                      <span>ร้านได้ {money(team.shopCut)}</span>
                      <span>WHT {money(team.wht)}</span>
                    </div>
                  </div>
                  <div className="te-ranking-net">
                    <small>{RANK_METRICS[rankMetric].short}</small>
                    <strong>{metricText(team, rankMetric)}</strong>
                    <span>ดูรายละเอียด →</span>
                  </div>
                </button>
              );
            })}
          </section>
        </>
      )}

      {selectedTeam && (
        <div className="te-modal-backdrop" onClick={() => setSelectedTeam("")}>
          <div className="te-modal" onClick={(e) => e.stopPropagation()}>
            <div className="te-modal-head">
              <div>
                <div className="te-section-kicker">TEAM DETAIL</div>
                <h2>{selectedTeam}</h2>
                {getTeamRealName(selectedTeam, realNameByTeam) && <p className="te-modal-real-name">{getTeamRealName(selectedTeam, realNameByTeam)}</p>}
                <p>รายละเอียดปี {Number(selectedYear) + 543}</p>
              </div>
              <button className="te-icon-btn" onClick={() => setSelectedTeam("")}>✕</button>
            </div>

            <div className="te-modal-summary">
              <div className="te-modal-summary-primary">
                <span>ทีมได้รับทั้งหมดทั้งปี</span>
                <strong>{money(selectedTeamYear.totalReceived)}</strong>
                <small>{fmt(selectedTeamYear.qty)} ใบ · {selectedTeamYear.count} ค่ากด · {selectedTeamYear.extraCount} ค่าคอม/พิเศษ</small>
              </div>
              <div>
                <span>ค่ากดสุทธิ</span>
                <strong>{money(selectedTeamYear.net)}</strong>
              </div>
              <div>
                <span>ค่าคอม / รายได้พิเศษ</span>
                <strong>{money(selectedTeamYear.extraNet)}</strong>
              </div>
              <div>
                <span>สร้างส่วนแบ่งให้ร้าน</span>
                <strong>{money(selectedTeamYear.shopCut)}</strong>
              </div>
              <div>
                <span>ค่ากดรวมก่อนหัก</span>
                <strong>{money(selectedTeamYear.gross)}</strong>
              </div>
              <div>
                <span>WHT รวม</span>
                <strong>{money(selectedTeamYear.totalWht)}</strong>
              </div>
            </div>

            <div className="te-modal-months">
              {selectedTeamMonths.map((month) => {
                const empty = month.totalCount === 0;
                const active = detailMonth === month.month;
                return (
                  <button
                    key={month.month}
                    disabled={empty}
                    className={active ? "is-active" : ""}
                    onClick={() => setDetailMonth((prev) => prev === month.month ? null : month.month)}
                  >
                    <span>{monthShort[month.month - 1]}</span>
                    <strong>{money(month.totalReceived)}</strong>
                    <small>ค่ากด {money(month.net)} · ค่าคอม/พิเศษ {money(month.extraNet)}</small>
                  </button>
                );
              })}
            </div>

            {detailMonthData && (
              <section className="te-detail-panel">
                <div className="te-detail-panel-head">
                  <div>
                    <strong>{monthNames[detailMonthData.month - 1]} {Number(selectedYear) + 543}</strong>
                    <span>{detailMonthData.count} ค่ากด · {detailMonthData.extraCount} ค่าคอม/พิเศษ · {fmt(detailMonthData.qty)} ใบ</span>
                  </div>
                  <div>
                    <small>ทีมได้รับทั้งหมด</small>
                    <strong>{money(detailMonthData.totalReceived)}</strong>
                  </div>
                </div>

                <div className="te-event-list">
                  {detailEvents.map((event) => (
                    <div className="te-event-row" key={event.name}>
                      <div>
                        <strong>{event.name}</strong>
                        <span>{fmt(event.qty)} ใบ · ร้านได้ {money(event.shopCut)} · WHT {money(event.wht)}</span>
                      </div>
                      <div className="te-event-values">
                        <small>สุทธิทีม</small>
                        <strong>{money(event.net)}</strong>
                      </div>
                    </div>
                  ))}
                </div>

                {detailMonthData.extras.length > 0 && (
                  <>
                    <div className="te-detail-subhead">ค่าคอม / รายได้พิเศษ</div>
                    <div className="te-event-list">
                      {detailMonthData.extras
                        .slice()
                        .sort((a, b) => b.__date - a.__date)
                        .map((item) => {
                          const amounts = extraAmounts(item);
                          return (
                            <div className="te-event-row te-event-row--extra" key={item.__id}>
                              <div>
                                <strong>{item.category || "รายได้พิเศษ"}</strong>
                                <span>{item.description || "ไม่มีรายละเอียด"} · WHT {money(amounts.wht)}</span>
                              </div>
                              <div className="te-event-values">
                                <small>ทีมได้รับ</small>
                                <strong>{money(amounts.net)}</strong>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </>
                )}
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
